'use client';

import { useState, useRef, useEffect } from 'react';
import { useOrderBook } from '@/hooks/useOrderBook';
import { useHyperliquidStore } from '@/stores/hyperliquidStore';

export default function OrderBook() {
  // UI State
  const [depth, setDepth] = useState(1);
  const [unit, setUnit] = useState<'USD' | 'COIN'>('USD');

  // Business logic from hook
  const {
    // Data
    asksWithTotal,
    bidsWithTotal,
    maxTotal,

    // Spread calculations
    spread,
    spreadPercent,
  } = useOrderBook();
  const metaAndAssetCtxs = useHyperliquidStore(state => state.metaAndAssetCtxs);
  const selectedAssetNumber = useHyperliquidStore(state => state.selectedAssetNumber);
  const baseTokenName = metaAndAssetCtxs[selectedAssetNumber]?.universe?.name;

  return (
    <div className='flex flex-1 shrink-0 flex-col overflow-hidden rounded-lg lg:w-[263px] lg:border'>
      <div className='flex flex-col justify-center gap-3 px-2 py-2 lg:py-2.5'>
        <h3 className='hidden text-xs leading-none font-medium lg:block'>OrderBook</h3>
        <div className='grid grid-cols-4 border-gray-800 text-xs leading-none lg:grid-cols-3'>
          <div className='text-gray-400 max-lg:order-1 max-lg:text-right'>Price</div>
          <div className='text-gray-400 max-lg:order-0 lg:text-right'>Size USD</div>
          <div className='text-left text-gray-400 max-lg:order-3 lg:hidden'>Price</div>
          <div className='text-right text-gray-400 max-lg:order-4 lg:hidden'>Size USD</div>
          <div className='hidden text-right text-gray-400 lg:block'>Total USD</div>
        </div>
      </div>

      <div className='flex flex-1 flex-row-reverse overflow-hidden max-lg:gap-0.5 lg:flex-col'>
        {/* Asks */}
        <div className='flex flex-1 flex-col gap-y-px overflow-y-auto lg:flex-col-reverse'>
          {asksWithTotal.map((ask, i) => {
            const depthPercent = (ask.accumulativeUSD / maxTotal) * 100;
            const currentPercent = (ask.usd / maxTotal) * 100;

            return (
              <div
                key={`${ask.price}-${ask.size}`}
                className='relative grid cursor-pointer grid-cols-2 px-3 py-1 text-xs hover:bg-gray-900/50 lg:grid-cols-3'
              >
                <div
                  className='absolute inset-y-0 bg-[#FF7C7C33] lg:left-0'
                  style={{ width: `${depthPercent}%` }}
                />
                {/* <div
                  className='absolute inset-y-0 left-0 bg-red-500/30'
                  style={{ width: `${currentPercent}%` }}
                /> */}
                <div className='z-10 font-mono text-[#F44E4E]'>{ask.price.toFixed(0)}</div>
                <div className='z-10 text-right font-mono text-[#EBEBEB]'>
                  {unit === 'USD' ? ask.usd.toFixed(0) : ask.size.toFixed(0)}
                </div>
                <div className='z-10 hidden text-right font-mono text-[#EBEBEB] lg:block'>
                  {unit === 'USD' ? ask.accumulativeUSD.toFixed(0) : ask.accumulativeSize.toFixed(0)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Spread */}
        <div className='hidden h-5 shrink-0 items-center justify-center gap-5 bg-[#EBEBEB33] text-[10px] text-white lg:flex'>
          <span className=''>Spread {depth} </span>
          <span className=''>{spread.toFixed(1)}</span>
          <span className=''>{spreadPercent.toFixed(3)}%</span>
        </div>

        {/* Bids */}
        <div className='flex flex-1 flex-col gap-y-px overflow-y-auto'>
          {bidsWithTotal.map((bid, i) => {
            const depthPercent = (bid.accumulativeUSD / maxTotal) * 100;
            const currentPercent = (bid.usd / maxTotal) * 100;

            return (
              <div
                key={`${bid.price}-${bid.size}`}
                className='relative grid cursor-pointer grid-cols-2 px-3 py-1 text-xs hover:bg-gray-900/50 lg:grid-cols-3'
              >
                <div
                  className='absolute inset-y-0 right-0 bg-[#7BD27B33] lg:left-0'
                  style={{ width: `${depthPercent}%` }}
                />
                {/* <div
                  className='absolute inset-y-0 left-0 bg-green-500/30'
                  style={{ width: `${currentPercent}%` }}
                /> */}
                <div className='z-10 font-mono text-[#00BE00] max-lg:order-1 max-lg:text-right'>
                  {bid.price.toFixed(0)}
                </div>
                <div className='z-10 font-mono lg:text-right lg:text-[#EBEBEB]'>
                  {unit === 'USD' ? bid.usd.toFixed(0) : bid.size.toFixed(0)}
                </div>
                <div className='z-10 hidden text-right font-mono text-[#EBEBEB] lg:block'>
                  {unit === 'USD' ? bid.accumulativeUSD.toFixed(0) : bid.accumulativeSize.toFixed(0)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
